import { createElement } from "./utils";

export const block_special_characters = (() => {
	const password = /[" ]/g;
	const cellphone = /[^0-9]+/g;
	const some = /[~`!@#$%^*()+={}\[\]|\\:;<>,.?±§]/g;
	const every = /[~`!@#$%^*()+={}\[\]|\\:;'<>,.?±§&/\\"]/g;
	const company = /[~`!@#$%^*()+={}\[\]|\\:;<>?±§\\]/g;

	const is_password = (node) => node.name.includes("password");

	const choose_regex = (node, all) => {
		if (node.name === "company") {
			return [company];
		}
		if (node.name === "cellphone_number") {
			return [cellphone, /^0/];
		}
		return [all ? every : some];
	};

	const block_characters = (regex, first) => (ev) => {
		const value = ev.target.value;

		if (
			(first && value.length === 0 && first.test(ev.key)) ||
			(ev.key !== "Backspace" && ev.key !== "Tab" && regex.test(ev.key))
		) {
			return ev.preventDefault();
		}
	};

	const prevent_paste = (regex) => (ev) => {
		const text = (ev.clipboardData || window.clipboardData).getData("text");
		if (regex.test(text)) ev.preventDefault();
	};

	const handle_input = (regex) => (ev) => {
		ev.target.value = ev.target.value.replace(regex, "");
	};

	return (node, { all = false } = {}) => {
		const [regex, first] = is_password(node)
			? [password]
			: choose_regex(node, all);

		const characters = block_characters(regex, first);
		const paste = prevent_paste(regex);
		const input = handle_input(regex);

		node.addEventListener("keydown", characters);
		node.addEventListener("paste", paste);
		node.addEventListener("input", input);

		return {
			destroy() {
				node.removeEventListener("keydown", characters);
				node.removeEventListener("paste", paste);
				node.removeEventListener("input", input);
			},
		};
	};
})();

export const validate = (() => {
	const getFormGroup = (group) => {
		do {
			if (group.className.includes("form-group")) {
				return group;
			}
			group = group.parentElement;
		} while (group && group.tagName !== "BODY");
	};

	const types = {
		cellphone: function (node, feedback) {
			const group = getFormGroup(node.parentElement);

			const blur = () => {
				const short = node.value.trim().length < node.minLength;
				const method = node.value.trim() && !short ? "remove" : "add";
				group?.classList[method]("field--invalid");

				if (!node.value.trim()) {
					feedback.textContent = "This field is required.";
					node.addEventListener("input", input);
					return group.appendChild(feedback);
				}

				if (short) {
					feedback.textContent = `Should be at least ${node.minLength} characters long.`;
					node.addEventListener("input", input);
					return group.appendChild(feedback);
				}
			};

			const input = () => {
				group?.classList.remove("field--invalid");

				if (node.value.trim()) {
					node.removeEventListener("input", input);
					group?.removeChild(feedback);
				}
			};

			node.addEventListener("blur", blur);

			return {
				destroy() {
					node.removeEventListener("blur", blur);
					node.removeEventListener("input", input);
				},
			};
		},
		text: function (node, feedback) {
			const group = getFormGroup(node.parentElement);

			const blur = () => {
				const method = node.value.trim() ? "remove" : "add";
				group?.classList[method]("field--invalid");

				if (!node.value.trim()) {
					feedback.textContent = "This field is required.";
					node.addEventListener("input", input);
					group.appendChild(feedback);
				}
			};

			const input = () => {
				group?.classList.remove("field--invalid");

				if (node.value.trim()) {
					node.removeEventListener("input", input);
					group?.removeChild(feedback);
				}
			};

			node.addEventListener("blur", blur);

			return {
				destroy() {
					node.removeEventListener("blur", blur);
					node.removeEventListener("input", input);
				},
			};
		},
		email: function (node, feedback) {
			const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/g;
			const group = getFormGroup(node.parentElement);

			const blur = () => {
				const value = node.value.trim();
				feedback.textContent = "Please provide a valid email address.";

				if (!value || !node.validity.valid) {
					group.classList.add("field--invalid");
					group.appendChild(feedback);
					node.addEventListener("input", input);
				} else if (!regex.test(value) || !node.validity.valid) {
					group.classList.add("field--invalid");
					group.appendChild(feedback);
					node.addEventListener("input", input);
				} else if (value && regex.test(value)) {
					group.classList.remove("field--invalid");
				}
			};

			const input = () => {
				group.classList.remove("field--invalid");

				if (node.value.trim()) {
					node.removeEventListener("input", input);
					group.removeChild(feedback);
				}
			};

			node.addEventListener("blur", blur);

			return {
				destroy() {
					node.removeEventListener("blur", blur);
				},
			};
		},
		checkbox: function (node, feedback) {
			const group = getFormGroup(node.parentElement);

			const blur = () => {
				feedback.textContent =
					"You need to accept the terms in order to continue.";

				if (node.checked) {
					group.classList.remove("field--invalid");
					feedback.remove();
				} else {
					group.classList.add("field--invalid");
					group.append(feedback);
				}
			};

			const change = () => {
				parent.classList.remove("field--invalid");

				if (node.checked) {
					group.classList.remove("field--invalid");
					feedback.remove();
				}
			};

			node.addEventListener("blur", blur);
			node.removeEventListener("change", change);

			return {
				destroy() {
					node.removeEventListener("blur", blur);
					node.removeEventListener("change", change);
				},
			};
		},
	};

	return (node, type) => {
		const feedback = createElement("p", { className: "field__feedback" });
		return types[type](node, feedback);
	};
})();

export function trapFocus(element) {
	const focusableEls = element.querySelectorAll(
		'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])'
	);
	const firstFocusableEl = focusableEls[0];
	const lastFocusableEl = focusableEls[focusableEls.length - 1];
	const KEYCODE_TAB = 9;

	return function trap(e) {
		var isTabPressed = e.key === "Tab" || e.keyCode === KEYCODE_TAB;

		if (!isTabPressed) {
			return;
		}

		if (e.shiftKey) {
			/* shift + tab */ if (document.activeElement === firstFocusableEl) {
				lastFocusableEl.focus();
				e.preventDefault();
			}
		} /* tab */ else {
			if (document.activeElement === lastFocusableEl) {
				firstFocusableEl.focus();
				e.preventDefault();
			}
		}
	};
}
